import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ORG_TOUR_ITINERARY_PDF_PLACEHOLDER_HE,
  resolveOrganizedTourItineraryContent,
} from "../../../utils/resolveOrganizedTourItineraryContent";
import { buildOrganizedTourResourceProxyUrl } from "../utils/buildOrganizedTourResourceProxyUrl";
import { resolveOrganizedTourPdfEmbedSrc } from "../utils/resolveOrganizedTourPdfEmbedSrc";
import "./OrganizedTourItineraryContentRenderer.css";

const ORG_TOUR_ITINERARY_EMBED_FAILED_HE =
  "לא ניתן להציג את מסלול הטיול בתוך המערכת";

function isExternalHttpUrl(src) {
  return /^https?:\/\//i.test(String(src || "").trim());
}

function PdfEmbed({ src, title, onEmbedFailed }) {
  return (
    <iframe
      className="ot-itin-content__pdf-iframe"
      src={src}
      title={title}
      aria-label={title}
      onError={() => onEmbedFailed?.()}
    />
  );
}

function PdfEmbedFailureActions({ directUrl, proxyUrl }) {
  const openUrl = directUrl || proxyUrl;
  const downloadUrl = directUrl || proxyUrl;
  return (
    <div className="ot-itin-content__pdf-fallback" role="region" aria-label="מסלול הטיול">
      <p className="ot-itin-content__pdf-placeholder">{ORG_TOUR_ITINERARY_EMBED_FAILED_HE}</p>
      <div className="ot-itin-content__pdf-actions">
        {openUrl ? (
          <a
            className="ot-itin-content__pdf-download ot-itin-content__pdf-download--secondary"
            href={openUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            פתיחת PDF בלשונית חדשה
          </a>
        ) : null}
        {downloadUrl ? (
          <a
            className="ot-itin-content__pdf-download"
            href={downloadUrl}
            target="_blank"
            rel="noopener noreferrer"
            download
          >
            הורדת PDF
          </a>
        ) : null}
      </div>
    </div>
  );
}

function PdfDownloadFallback({ downloadUrl }) {
  return (
    <div className="ot-itin-content__pdf-fallback" role="region" aria-label="מסלול הטיול">
      <p className="ot-itin-content__pdf-placeholder">{ORG_TOUR_ITINERARY_PDF_PLACEHOLDER_HE}</p>
      {downloadUrl ? (
        <a
          className="ot-itin-content__pdf-download"
          href={downloadUrl}
          target="_blank"
          rel="noopener noreferrer"
          download
        >
          הורדת PDF
        </a>
      ) : null}
    </div>
  );
}

function ProxiedExternalPdfEmbed({ resourceUrl }) {
  const proxySrc = useMemo(
    () => buildOrganizedTourResourceProxyUrl(resourceUrl),
    [resourceUrl]
  );
  const embedSrc = useMemo(
    () => resolveOrganizedTourPdfEmbedSrc(resourceUrl),
    [resourceUrl]
  );
  const [embedFailed, setEmbedFailed] = useState(false);

  const handleEmbedFailed = useCallback(() => {
    setEmbedFailed(true);
    if (import.meta.env?.DEV) {
      // eslint-disable-next-line no-console
      console.info("[ORG_TOUR_ITINERARY_PDF_EMBED_FAILED]", {
        resourceUrl: String(resourceUrl || "").slice(0, 120),
        embedSrc: embedSrc ? embedSrc.slice(0, 120) : null,
        proxySrc: proxySrc ? proxySrc.slice(0, 120) : null,
      });
    }
  }, [resourceUrl, embedSrc, proxySrc]);

  useEffect(() => {
    setEmbedFailed(false);
  }, [embedSrc]);

  if (!embedSrc || embedFailed) {
    return (
      <PdfEmbedFailureActions
        directUrl={resourceUrl}
        proxyUrl={proxySrc}
      />
    );
  }

  return (
    <div className="ot-itin-content__embed-stack">
      <PdfEmbed
        src={embedSrc}
        title="מסלול הטיול"
        onEmbedFailed={handleEmbedFailed}
      />
      {resourceUrl ? (
        <div className="ot-itin-content__pdf-actions">
          <a
            className="ot-itin-content__pdf-download ot-itin-content__pdf-download--secondary"
            href={resourceUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            פתיחת PDF בלשונית חדשה
          </a>
          <a
            className="ot-itin-content__pdf-download ot-itin-content__pdf-download--inline"
            href={resourceUrl}
            target="_blank"
            rel="noopener noreferrer"
            download
          >
            הורדת PDF
          </a>
        </div>
      ) : null}
    </div>
  );
}

export function OrganizedTourItineraryContentRenderer({
  contentText,
  contentHtmlSanitized,
  resourceUrl,
  resourceKind,
  sourcePath = "profile",
  compact = false,
}) {
  const model = useMemo(
    () =>
      resolveOrganizedTourItineraryContent({
        contentText,
        contentHtmlSanitized,
        resourceUrl,
        resourceKind,
        sourcePath,
      }),
    [contentText, contentHtmlSanitized, resourceUrl, resourceKind, sourcePath]
  );

  const [blobUrl, setBlobUrl] = useState(null);

  useEffect(() => {
    if (model.renderMode !== "pdf_blob" || !model.pdfBytes) {
      setBlobUrl(null);
      return undefined;
    }
    const blob = new Blob([model.pdfBytes], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);
    setBlobUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [model.renderMode, model.pdfBytes]);

  if (model.renderMode === "none") return null;

  if (model.renderMode === "text_plain" && model.plainText) {
    return (
      <div
        className={`ot-itin-content__text${compact ? " ot-itin-content__text--preview" : ""}`}
      >
        {model.plainText}
      </div>
    );
  }

  if (model.renderMode === "html_sanitized" && model.sanitizedHtml) {
    return (
      <div
        className="ot-itin-content__html"
        dir="rtl"
        lang="he"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: model.sanitizedHtml }}
      />
    );
  }

  if (model.renderMode === "embed_url" && model.embedUrl) {
    if (isExternalHttpUrl(model.embedUrl)) {
      return <ProxiedExternalPdfEmbed resourceUrl={model.embedUrl} />;
    }
    return (
      <div className="ot-itin-content__embed-stack">
        <PdfEmbed src={model.embedUrl} title="מסלול הטיול" />
      </div>
    );
  }

  if (model.renderMode === "pdf_blob" && blobUrl) {
    return <PdfEmbed src={blobUrl} title="מסלול הטיול" />;
  }

  if (model.renderMode === "pdf_download_fallback" || (model.renderMode === "pdf_blob" && !blobUrl)) {
    return <PdfDownloadFallback downloadUrl={model.pdfDownloadUrl} />;
  }

  return null;
}
